// src/pages/OrgAdminConsole.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { LogoUpload } from "@/components/admin/LogoUpload";

// Category options (same as AdminOrganizationsTable)
const CATEGORY_OPTIONS = [
  { value: "Telecom (Fixed/ISP)", label: "Telecommunications" },
  { value: "Risk Exposure", label: "Risk Exposure" },
  { value: "Media", label: "Media" },
  { value: "Private Equity", label: "Private Equity" },
  { value: "Data Center", label: "Data Center" },
  { value: "Gen AI", label: "Gen AI" },
];

// Default questionnaire mapping by category
const CATEGORY_QUESTIONNAIRE_MAP: Record<string, string> = {
  "MNO": "telecom_v2",
  "Telecom (Fixed/ISP)": "telecom_v2",
  "Media": "telecom_v2",
  "Private Equity": "telecom_v2",
  "Data Center": "telecom_v2",
  "Gen AI": "ai_llm_self_v1",
  "Risk Exposure": "hrv_self_v1",
};

interface QuestionnaireOption {
  questionnaire_id: string;
  label: string;
}

export default function OrgAdminConsole() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { role: effectiveRole } = useEffectiveRole();
  const [roles, setRoles] = useState<string[] | null>(null);
  const [orgs, setOrgs] = useState<
    {
      id: string;
      name: string;
      category: string | null;
      region: string | null;
      sector: string | null;
      archived: boolean | null;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // create-organization state
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createName, setCreateName] = useState("");
  const [createCategory, setCreateCategory] = useState("");
  const [createQuestionnaireId, setCreateQuestionnaireId] = useState("");
  const [questionnaireOptions, setQuestionnaireOptions] = useState<QuestionnaireOption[]>([]);
  const [createLogoPath, setCreateLogoPath] = useState("");

  // Redirect client_admin to their proper landing page
  useEffect(() => {
    if (effectiveRole === "client_admin") {
      navigate("/client/sub-orgs", { replace: true });
    }
  }, [effectiveRole, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        // 1) current user
        const { data: userRes, error: userErr } = await supabase.auth.getUser();
        if (userErr || !userRes?.user) {
          throw new Error("Not authenticated");
        }
        const user = userRes.user;

        // 2) optional roles for debug
        try {
          const { data: rolesRes, error: rolesErr } = await supabase.rpc(
            "current_user_roles"
          );
          if (!cancelled) {
            setRoles(rolesErr ? [] : (rolesRes ?? []).map((r) => r.toString()));
          }
        } catch {
          if (!cancelled) setRoles([]);
        }

        // 3) portfolios this user can administer
        const { data, error } = await (supabase as any)
          .from("org_memberships")
          .select(
            `
            org_id,
            portfolios!inner (
              id,
              name,
              category,
              region,
              sector,
              archived
            )
          `
          )
          .eq("user_id", user.id)
          .eq("member_role", "org_admin")
          .eq("portfolios.archived", false);

        if (error) throw error;

        const mapped =
          (data ?? [])
            .map((row: any) => row.portfolios)
            .filter(Boolean)
            .map((o: any) => ({
              id: o.id,
              name: o.name,
              category: o.category ?? null,
              region: o.region ?? null,
              sector: o.sector ?? null,
              archived: o.archived ?? null,
            })) ?? [];

        if (!cancelled) {
          setOrgs(mapped);
        }

        console.log("org_admin orgs data:", data);
        console.log("org_admin mapped orgs:", mapped);
      } catch (e: any) {
        if (!cancelled) setErrorMsg(e?.message || "Failed to load org-admin data");
        console.error("OrgAdminConsole init error", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle category change - fetch questionnaire options if needed
  const handleCategoryChange = async (value: string) => {
    setCreateCategory(value);
    setCreateQuestionnaireId("");
    setQuestionnaireOptions([]);

    if (value === "Telecom (Fixed/ISP)") {
      try {
        const { data, error } = await supabase
          .from('v_org_category_questionnaires')
          .select('questionnaire_id, label')
          .eq('category', value);

        if (error) {
          console.error('Error fetching questionnaires:', error);
          // Fallback to default
          setCreateQuestionnaireId(CATEGORY_QUESTIONNAIRE_MAP[value] || "telecom_v2");
          return;
        }

        setQuestionnaireOptions(data || []);
        
        // Default to telecom_v2 if available, otherwise first option
        const defaultOption = (data || []).find(d => d.questionnaire_id === 'telecom_v2')?.questionnaire_id
                            || (data && data[0]?.questionnaire_id) || 'telecom_v2';
        setCreateQuestionnaireId(defaultOption);
      } catch (error) {
        console.error('Error fetching questionnaires:', error);
        setCreateQuestionnaireId(CATEGORY_QUESTIONNAIRE_MAP[value] || "telecom_v2");
      }
    } else {
      // For other categories, use the default mapping
      setCreateQuestionnaireId(CATEGORY_QUESTIONNAIRE_MAP[value] || "telecom_v2");
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    
    if (!createName.trim()) {
      setCreateError("Organization name is required.");
      return;
    }
    if (!createCategory) {
      setCreateError("Category is required.");
      return;
    }

    const questionnaireId = createQuestionnaireId || CATEGORY_QUESTIONNAIRE_MAP[createCategory] || "telecom_v2";

    setCreating(true);
    try {
      const { data: userRes, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userRes?.user) {
        throw new Error("Not authenticated");
      }
      const user = userRes.user;

      // 1) create org with questionnaire_id
      const payload: any = {
        name: createName.trim(),
        category: createCategory,
        questionnaire_id: questionnaireId,
        archived: false,
      };
      if (createLogoPath) payload.logo_url = createLogoPath;

      console.log("CSV Pro – Creating portfolio with payload:", payload);

      const { data: portfolio, error: portfolioErr } = await (supabase as any)
        .from("portfolios")
        .insert(payload)
        .select("id, name, category, region, sector, archived")
        .single();

      console.log("CSV Pro – Insert result:", { data: portfolio, error: portfolioErr });

      if (portfolioErr) throw portfolioErr;

      // 2) add creator as org_admin member
      const { error: memErr } = await (supabase as any)
        .from("org_memberships")
        .insert({
          org_id: portfolio.id,
          user_id: user.id,
          member_role: "org_admin",
        });

      if (memErr) {
        console.warn("[OrgAdminConsole] membership insert error", memErr);
      }

      // 3) update local state
      setOrgs((prev) => [...prev, portfolio]);

      toast({
        title: "Success",
        description: `Portfolio "${portfolio.name}" created successfully.`,
      });

      // clear form
      setCreateName("");
      setCreateCategory("");
      setCreateQuestionnaireId("");
      setQuestionnaireOptions([]);
      setCreateLogoPath("");
    } catch (e: any) {
      console.error("[OrgAdminConsole] create org error", e);
      setCreateError(e?.message ?? "Failed to create organization.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <AppLayout title="Organization Admin">
      <div className="p-6 space-y-4">
        {effectiveRole === "client_admin" && (
          <div className="mb-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/client/sub-orgs")}
            >
              Sub-Organizations
            </Button>
          </div>
        )}

        {errorMsg && (
          <Alert variant="destructive">
            <AlertDescription>{errorMsg}</AlertDescription>
          </Alert>
        )}

        {/* Create Organization card */}
        <Card className="p-4 space-y-4">
          <h2 className="font-semibold text-lg">Create a new organization</h2>
          <p className="text-sm text-muted-foreground">
            As an organization admin, you can create organizations and manage
            their users and assessments.
          </p>
          <form onSubmit={handleCreateOrg} className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2 space-y-1">
              <Label htmlFor="org-name">Organization name *</Label>
              <Input
                id="org-name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Example Telecom Ltd."
                required
              />
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="org-category">Category *</Label>
              <Select value={createCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {questionnaireOptions.length > 0 && (
              <div className="space-y-1">
                <Label htmlFor="org-questionnaire">Questionnaire *</Label>
                <Select value={createQuestionnaireId} onValueChange={setCreateQuestionnaireId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select questionnaire" />
                  </SelectTrigger>
                  <SelectContent>
                    {questionnaireOptions.map((opt) => (
                      <SelectItem key={opt.questionnaire_id} value={opt.questionnaire_id}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="md:col-span-2">
              <LogoUpload
                onUploaded={setCreateLogoPath}
                onFileSelected={() => {}}
              />
            </div>

            <div className="md:col-span-2 flex flex-col gap-2">
              {createError && (
                <p className="text-xs text-destructive">{createError}</p>
              )}
              <Button type="submit" disabled={creating || !createCategory} className="self-start">
                {creating ? "Creating…" : "Create Organization"}
              </Button>
            </div>
          </form>
        </Card>

        {/* Organizations list */}
        <Card className="p-4">
          <h2 className="font-semibold text-lg mb-2">
            Organizations you can administer
          </h2>

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : orgs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No organizations found for your org_admin membership.
            </p>
          ) : (
            <div className="space-y-2">
              {orgs.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center justify-between rounded-md border p-3"
                >
                  <div>
                    <div className="font-medium">{o.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {o.category ?? "Uncategorized"}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/org-admin/${o.id}/users`)}
                      variant="outline"
                      size="sm"
                    >
                      Manage Users
                    </Button>
                    <Button
                      onClick={() => navigate(`/org-admin/${o.id}/assessments`)}
                      variant="outline"
                      size="sm"
                    >
                      View Assessments
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
